// FILE: frontend/src/components/kyb/sections/CompaniesHouseSection.tsx
// COMPLETE VERSION: Properly extract and display all Companies House data including offices
// Compatible with existing data structure

import React from 'react';
import { Building2, Calendar, MapPin, ExternalLink, FileText } from 'lucide-react';
import type { CompaniesHouseData } from '../../../types/kyb.types';

interface CompaniesHouseSectionProps {
  companiesHouseData: CompaniesHouseData | null;
}

export const CompaniesHouseSection: React.FC<CompaniesHouseSectionProps> = ({ 
  companiesHouseData 
}) => {
  if (!companiesHouseData || !companiesHouseData.company_number) {
    return null;
  }

  // Extract profile data (may be nested)
  const profile = (companiesHouseData as any).profile || companiesHouseData;
  
  // Extract registered office address
  const registeredOffice = profile.registered_office_address || null;
  
  // Format address into lines
  const formatAddress = (address: any): string[] => {
    if (!address) return [];
    
    const lines: string[] = [];
    if (address.address_line_1) lines.push(address.address_line_1);
    if (address.address_line_2) lines.push(address.address_line_2);
    if (address.locality) lines.push(address.locality);
    if (address.region) lines.push(address.region);
    if (address.postal_code) lines.push(address.postal_code);
    if (address.country) lines.push(address.country);
    
    return lines.filter(Boolean);
  };

  const officeAddressLines = formatAddress(registeredOffice);
  
  // Get SIC codes
  const sicCodes = profile.sic_codes || [];

  return (
    <div className="bg-gradient-to-r from-blue-600 to-purple-700 rounded-2xl p-1 shadow-2xl">
      <div className="bg-white rounded-[18px] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-700 px-6 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
            <div className="flex items-center gap-3">
              <Building2 className="w-6 h-6 text-white" />
              <h3 className="text-lg sm:text-xl font-bold text-white"> 
                Official UK Companies House Data
              </h3>
            </div>
            <span className="px-3 py-1 bg-green-500 text-white text-sm font-bold rounded-full">
              {(companiesHouseData.status || profile.company_status || 'ACTIVE').toUpperCase()}
            </span>
          </div>
          <p className="text-blue-100 text-sm mt-1">Verified government registry information</p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Company Number Section */}
          {(companiesHouseData.company_number || profile.company_number) && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-slate-50 rounded-lg gap-3 sm:gap-0"> 
              <div className="flex-1">
                <p className="text-xs font-semibold text-slate-600 uppercase">UK Company Number</p>
                <p className="text-xl sm:text-2xl font-bold text-slate-900 mt-1">
                  {companiesHouseData.company_number || profile.company_number}
                </p>
              </div>
              {(companiesHouseData.companies_house_url || (companiesHouseData.company_number || profile.company_number)) && (
                <a
                  href={companiesHouseData.companies_house_url || 
                        `https://find-and-update.company-information.service.gov.uk/company/${companiesHouseData.company_number || profile.company_number}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Official Record
                </a>
              )}
            </div>
          )}

          {/* Basic Information Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(companiesHouseData.incorporation_date || profile.date_of_creation) && (
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-600 uppercase">Incorporated</p>
                  <p className="text-lg font-bold text-slate-900 mt-1">
                    {new Date(companiesHouseData.incorporation_date || profile.date_of_creation).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            )}

            {(companiesHouseData.company_type || profile.company_type) && (
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Building2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-600 uppercase">Company Type</p>
                  <p className="text-lg font-bold text-slate-900 mt-1">
                    {companiesHouseData.company_type || profile.company_type}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Registered Office Address */}
          {registeredOffice && officeAddressLines.length > 0 && (
            <div className="border-t border-slate-200 pt-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 bg-green-100 rounded-lg">
                  <MapPin className="w-5 h-5 text-green-600" />
                </div>
                <h4 className="text-lg font-bold text-slate-900">Registered Office Address</h4>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                {officeAddressLines.map((line, index) => (
                  <p key={index} className="text-slate-700">
                    {line}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* SIC Codes */}
          {sicCodes && sicCodes.length > 0 && (
            <div className="border-t border-slate-200 pt-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
                <h4 className="text-lg font-bold text-slate-900">Industry Classification (SIC)</h4>
              </div>
              
              <div className="space-y-2">
                {sicCodes.map((code: string, index: number) => (
                  <div key={index} className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-700">{code}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};